import React, { useEffect, useState } from 'react';
import { useRequireCourseManage } from '@/hooks/admin/Courses/Course/useRequireCourseManage';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './CourseModulesPage.module.css';
import { useCourseBuilder } from './hooks/useCourseBuilder';
import { useModuleLessons } from './hooks/useModuleLessons';
import { AnimatePresence } from 'framer-motion';
import { UndoToast } from '@/components/shared/UndoToast/UndoToast';
import { ModulesEditor } from './ModulesEditor/ModulesEditor';
import { ModulesContentSidebar } from './ModulesContentSidebar/ModulesContentSidebar';
import { useMultilang } from '@/hooks/useMultilang';
import EyeIcon from '../../../../../../public/assets/icons/eye-open-white.svg?react';
import SetWhiteIcon from '../../../../../../public/assets/icons/set-white.svg?react';


import { tr } from '@/i18n';
const COURSES_URL = '/admin/learning/courses/manage';

export const CourseModulesPage: React.FC = () => {
  const { id: courseId } = useParams<{ id: string }>();
  useRequireCourseManage(courseId); // UAT DEF-EN-001: builder pages are for people who manage this course
  const navigate = useNavigate();
  const { ml } = useMultilang();

  const {
    course, isLoading, error,
    addModule, deleteModule, undoDeleteModule,
    duplicateModule, updateModuleProperty,
  } = useCourseBuilder(courseId);

  const {
    getLessons,
    isModuleLoading,
    fetchLessonsForModule,
    addLesson,
    updateLessonTitle,
    deleteLesson,
  } = useModuleLessons();

  const [toastInfo, setToastInfo] = useState<{
    id: number; message: string; onUndo: () => void;
  } | null>(null);

  useEffect(() => {
    if (course?.modules) {
      course.modules.forEach(module => {
        fetchLessonsForModule(module.id);
      });
    }
  }, [course?.modules]);

  const handleDeleteModule = (moduleId: string) => {
    const deleted = deleteModule(moduleId);
    if (deleted) {
      setToastInfo({
        id: Date.now(),
        message: tr('ui.module_x_deleted', 'Module "{v0}" deleted.', { v0: deleted.title }),
        onUndo: () => { undoDeleteModule(); setToastInfo(null); },
      });
    }
  };

  if (isLoading) return (
    <div className={styles.stateContainer}>
      <div className={styles.spinner} />
      <span className={styles.stateText}>{tr("ui.loading", "Loading...")}</span>
    </div>
  );

  if (error) return (
    <div className={styles.stateContainer}>
      <span className={styles.stateError}>{error}</span>
    </div>
  );

  if (!course) return (
    <div className={styles.stateContainer}>
      <span className={styles.stateText}>{tr("ui.course_not_found_d6ca79", "Course not found.")}</span>
    </div>
  );

  return (
    <>
      <div className={styles.pageContainer}>
        <div className={styles.topBarWrapper}>
          <div className={styles.topBarRow}>
            <nav className={`${styles.breadcrumb} subtitle-2`}>
              <span onClick={() => navigate('/admin/dashboard/learning/courses')} className={styles.breadcrumbLink}>{tr("breadcrumb.dashboard", "Dashboard")}</span>
              <span className={styles.breadcrumbSep}>/</span>
              <span onClick={() => navigate('/admin/dashboard/learning/courses')} className={styles.breadcrumbLink}>{tr("nav.learning", "Learning")}</span>
              <span className={styles.breadcrumbSep}>/</span>
              <span onClick={() => navigate(COURSES_URL)} className={styles.breadcrumbLink}>{tr("ui.course_management", "Course Management")}</span>
              <span className={styles.breadcrumbSep}>/</span>
              <span onClick={() => navigate(`/admin/dashboard/learning/courses/build/${courseId}`)} className={styles.breadcrumbLink}>{tr("ui.course_page", "Course page")}</span>
              <span className={styles.breadcrumbSep}>/</span>
              <span className={styles.breadcrumbCurrent}>{tr("admin.course_categories.kpi_modules", "Modules")}</span>
            </nav>
            <div className={styles.topBarRight}>
              <div className={styles.topBarGroup}>
                <button className={`${styles.topBarBtn} button`}>
                  <EyeIcon className={styles.btnIcon} /> {tr("common.preview", "Preview")}
                </button>
                <button className={`${styles.topBarBtn} button`}>
                  <SetWhiteIcon className={styles.btnIcon} /> {tr("edit_report.nav_general", "General")}
                </button>
              </div>
              <div className={styles.topBarGroup}>
                <button className={`${styles.saveBtn} button`}>{tr("lang.editor_save", "Save")}</button>
                <button className={`${styles.publishBtn} button`}>{tr("ui.publish", "Publish")}</button>
              </div>
            </div>
          </div>
          <div className={styles.titleBlock}>
            <span className={`${styles.draftBadge} subtitle-2`}>{tr("insight.card_status_draft", "Draft")}</span>
            <h1 className={`${styles.courseTitle} h1`}>{ml(course.fullname) || tr("ui.untitled_course", "Untitled Course")}</h1>
          </div>
        </div>

        <div className={styles.body}>
          <ModulesEditor
            courseId={courseId ?? ''}
            modules={course.modules}
            getLessons={getLessons}
            isModuleLoading={isModuleLoading}
            onAddModule={() => addModule(tr('ui.new_module', 'New Module'))}
            onDeleteModule={handleDeleteModule}
            onDuplicateModule={duplicateModule}
            onUpdateModuleTitle={(id, title) => updateModuleProperty(id, 'title', title)}
            onAddLesson={addLesson}
            onUpdateLessonTitle={updateLessonTitle}
            onDeleteLesson={deleteLesson}
            onBack={() => navigate(`/admin/dashboard/learning/courses/build/${courseId}`)}
          />
          <div className={styles.sidebarWrapper}>
            <ModulesContentSidebar
              modules={course.modules}
              getLessons={getLessons}
              onClose={() => navigate(`/admin/dashboard/learning/courses/build/${courseId}`)}
              showActions={false}
            />
          </div>
        </div>
      </div>

      <div className={styles.toastContainer}>
        <AnimatePresence>
          {toastInfo && (
            <UndoToast
              key={toastInfo.id}
              message={toastInfo.message}
              onUndo={toastInfo.onUndo}
              onDismiss={() => setToastInfo(null)}
            />
          )}
        </AnimatePresence>
      </div>

      <footer className={`${styles.footer} subtitle-2`}>
        {tr("ui.2025_ilearn_me_all_rights_reserved", "© 2025 iLearn Me. All rights reserved.")}
      </footer>
    </>
  );
};